/**
 * IndustryExplorer — horizontal scroll of segment chips on the home
 * dashboard. Each chip links to the matching segment landing page.
 *
 * Reuses the existing `SEGMENTS` data + `DomainIcon` palette so the
 * advanced UI and the regular dashboard stay visually aligned.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SEGMENTS } from '@/data/segments';
import { cn } from '@/utils/cn';

export function IndustryExplorer() {
  const { t } = useTranslation('dashboard');
  // Show the Tier-1 + Tier-2 segments first; ready ones at the front so
  // the user always sees something they can actually click.
  const ordered = [...SEGMENTS].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'ready' ? -1 : 1;
    return a.tier - b.tier;
  });

  return (
    <section aria-labelledby="industries-heading" className="mt-12">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 id="industries-heading" className="text-base font-semibold tracking-tight">
          {t('industries.title')}
        </h2>
        <p className="text-xs text-ink-muted">{t('industries.subtitle')}</p>
      </div>

      <div
        className={cn(
          'no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0',
          'snap-x snap-mandatory',
        )}
      >
        {ordered.map((segment) => {
          const Icon = segment.Icon;
          return (
            <Link
              key={segment.id}
              to={`/marketplace?seg=${segment.id}`}
              className={cn(
                'snap-start shrink-0 rounded-lg border border-border bg-surface px-4 py-3 shadow-subtle',
                'flex items-center gap-2 text-sm font-medium text-ink',
                'transition-shadow hover:shadow-raised hover:border-border-strong',
                segment.status === 'coming-soon' && 'opacity-60',
              )}
            >
              <Icon className="h-4 w-4 text-ink-muted" aria-hidden />
              <span>{t(`segments:${segment.id}.name`, { defaultValue: segment.id })}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
